import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Seo from "../Components/Seo.jsx";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import {
  FaBookOpen,
  FaUsers,
  FaUniversity,
  FaHandshake,
  FaChild,
} from "react-icons/fa";
import { HiOutlineAcademicCap } from "react-icons/hi";

import CustomButton from "../Components/Button.jsx";
import ContactUs from "../Components/ContactUs.jsx";
import BlogSection from "../Components/BlogSection.jsx";
import SocialSection from "../Components/SocialSection.jsx";
import TamilDecor from "../Components/TamilDecor.jsx";
import GoogleReviews from "../Components/GoogleReviews.jsx";
import {
  aboutData,
  aboutTopics,
  heroSlides,
  programStages,
} from "../constants/data.jsx";
import logo from "../assets/Images/logo.png";
import school from "../assets/Images/4.jpg";
import hero1 from "../assets/Images/1.jpeg";
import about1 from "../assets/Images/3.jpg";
import about2 from "../assets/Images/about-lamp-1.jpg";
import about3 from "../assets/Images/about-lamp-2.jpg";
import about4 from "../assets/Images/about-lamp-3.jpg";
import about5 from "../assets/Images/1.jpeg";
import visionIcon from "../assets/Images/vision.png";
import missionIcon from "../assets/Images/mission-blue.png";
import valuesIcon from "../assets/Images/values.png";
import leaf from "../assets/Images/leaf.png";
import defaultImg from "../assets/Images/event-default.jpg";
import api from "../config/axiosConfig.jsx";
import { buildEventPath } from "../utils/eventSlug.js";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";

// Import Framer Motion
import { motion, useInView } from "framer-motion";

const allImages = [about1, about2, about3, about4, about5];

const imageAlts = {
  [about1]: "Committee members lighting the traditional lamp at the WSTSC inauguration",
  [about2]: "Lighting the traditional lamp at the WSTSC inauguration ceremony",
  [about3]: "Teachers and committee members at the WSTSC inauguration ceremony",
  [about4]: "Families and teachers lighting the lamp in the school hall at a WSTSC ceremony",
  [about5]: "WSTSC students in traditional dress seated with their families at a school gathering",
};

const exploreLinks = [
  {
    to: "/tamil-classes-sydney/",
    icon: "bi-geo-alt",
    title: "Tamil classes in Sydney",
    text: "Who our classes suit, what children learn and where we meet.",
  },
  {
    to: "/programs/",
    icon: "bi-layers",
    title: "Programs and levels",
    text: "Our Preschool to Year 12 learning stages and teaching approach.",
  },
  {
    to: "/enrolment/",
    icon: "bi-pencil-square",
    title: "How to enrol",
    text: "Three simple steps, starting with an online expression of interest.",
  },
  {
    to: "/contact/",
    icon: "bi-telephone",
    title: "Contact and venue",
    text: "Call, email or find us at Walters Road Public School.",
  },
];

// Helper function to get initials from full name
const getInitials = (fullName) => {
  if (!fullName) return "?";
  const nameParts = fullName.trim().split(" ");
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  const firstInitial = nameParts[0].charAt(0).toUpperCase();
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  return `${firstInitial}${lastInitial}`;
};

// Avatar Component with initials
const AvatarWithInitials = ({ name, photoUrl, size = "100%" }) => {
  const [imageError, setImageError] = useState(false);
  const initials = getInitials(name);

  if (!photoUrl || imageError) {
    // Generate a consistent pastel color based on name
    const getColorFromName = (name) => {
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = hash % 360;
      return `hsl(${hue}, 70%, 85%)`;
    };

    return (
      <div
        className="d-flex align-items-center justify-content-center fw-bold"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: getColorFromName(name),
          color: "#333",
          fontSize: size === "100%" ? "3rem" : "1.5rem",
        }}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={photoUrl}
      className="img-fluid w-100 h-100"
      style={{ objectFit: "cover" }}
      alt={name}
      onError={() => setImageError(true)}
    />
  );
};

const Home = () => {
  const [teachers, setTeachers] = useState([]);
  const [principal, setPrincipal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const aboutRef = useRef(null);
  const teamRef = useRef(null);
  const eventsRef = useRef(null);
  const contactRef = useRef(null);
  // const goalRef = useRef(null);

  const aboutInView = useInView(aboutRef, { once: true, threshold: 0.1 });
  const teamInView = useInView(teamRef, { once: true, threshold: 0.1 });
  const eventsInView = useInView(eventsRef, { once: true, threshold: 0.1 });
  const contactInView = useInView(contactRef, { once: true, threshold: 0.1 });

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Handle navigation (normal route + section scroll) - Same as header
  const handleNavClick = (href) => {
    if (href.startsWith("#")) {
      const sectionId = href.substring(1);
      const section = document.getElementById(sectionId);
      if (section) section.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(href);
    }
  };

  // Fetch teachers from API
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoading(true);
        const response = await api.get("/teachers/active", {
          
        });
        console.log("teachers response:", response.data);
        if (response.data.success && response.data.data) {
          const allStaff = response.data.data;

          // Find principal
          const principalStaff = allStaff.find(
            (staff) => staff.position === "principal",
          );
          setPrincipal(principalStaff || null);

          // Find all teachers (excluding principal)
          const teachersList = allStaff.filter(
            (staff) => staff.position === "teacher",
          );
          setTeachers(teachersList);
        }
      } catch (error) {
        console.error("Error fetching teachers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, []);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const response = await api.get(
          "/website/articles/type/Event/status/active",
        );

        if (response.data.success && response.data.data?.articles) {
          setEvents(response.data.data.articles);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Scroll to a section requested by another page (e.g. Header nav while on
  // Event Details). Waits for teachers/events data to settle first, since
  // sections below them shift height once their spinners resolve into content.
  useEffect(() => {
    const targetId = location.state?.scrollTo || location.hash.slice(1);
    if (!targetId || loading || eventsLoading) return;

    const raf = requestAnimationFrame(() => {
      const section = document.getElementById(targetId);
      if (section) section.scrollIntoView({ behavior: "smooth" });
    });

    // Clear the scroll intent so it doesn't re-fire on later re-renders/back-nav
    navigate(location.pathname, { replace: true, state: {} });

    return () => cancelAnimationFrame(raf);
  }, [
    location.state,
    location.hash,
    loading,
    eventsLoading,
    navigate,
    location.pathname,
  ]);

  // Get the first uploaded image for an event, falling back to a default
  const getEventImage = (event) => {
    const imageAsset = event?.assets?.find((asset) => asset.asset_type === "img");
    return imageAsset?.asset_value || defaultImg;
  };

  // Split an event date into { day: "07", month: "JUN" } for the badge.
  // Reads back the literal date the API sent (no timezone conversion) - see
  // the note above formatEventDate in EventDetails.jsx.
  const getEventDateParts = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return { day: "--", month: "" };
    return {
      day: date.toLocaleDateString("en-AU", {
        timeZone: "UTC",
        day: "2-digit",
      }),
      month: date.toLocaleDateString("en-AU", {
        timeZone: "UTC",
        month: "short",
      }),
    };
  };

  const truncateText = (text, limit) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  // About
  // Helper: pick 2 different images
  const getTwoDistinctImages = () => {
    if (allImages.length === 1) {
      return [allImages[0], allImages[0]];
    }

    const firstIndex = Math.floor(Math.random() * allImages.length);
    let secondIndex = Math.floor(Math.random() * allImages.length);

    while (secondIndex === firstIndex) {
      secondIndex = Math.floor(Math.random() * allImages.length);
    }

    return [allImages[firstIndex], allImages[secondIndex]];
  };

  const [[mainImg, smallImg], setImages] = React.useState(() =>
    getTwoDistinctImages(),
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      const [newMain, newSmall] = getTwoDistinctImages();
      setImages([newMain, newSmall]);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Grade Section Tabs
  const topicIcons = {
    approach: [<FaBookOpen />, <HiOutlineAcademicCap />],
    teachers: [<FaUsers />, <FaUsers />],
    governance: [<FaUniversity />, <FaUniversity />],
    community: [<FaHandshake />, <FaHandshake />],
    "child-safety": [<FaChild />, <FaChild />],
  };

  const tabs = [
    ...aboutTopics.map((topic) => ({
      ...topic,
      icon: topicIcons[topic.id][0],
      mainIcon: topicIcons[topic.id][1],
    })),
    {
      id: "programs",
      icon: <HiOutlineAcademicCap />,
      title: "Programs Offered",
      isProgramTab: true,
      content: (
        <div>
          <div className="row g-3">
            {programStages.map((item, index) => (
              <div className="col-12 col-lg-4" key={index}>
                <div className="card h-100 border rounded-4 border-bottom-left">
                  <div className="card-body p-4">
                    <div className="d-flex flex-column justify-content-between gap-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="fw-bold text-uppercase mb-1">
                          {item.years}
                        </h6>
                        <span className="text-dark">{item.stage}</span>
                      </div>

                      <div className="flex-grow-1">
                        <p className="mb-0 text-muted">{item.focus}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  const [activeId, setActiveId] = useState(tabs[0].id);
  const activeTab = tabs.find((t) => t.id === activeId);

  return (
    <>
      <Seo path="/" />

      <div className="container-fluid p-0">
        {/* Hero Section */}
        <section id="home">
          <div
            id="heroCarousel"
            className="carousel slide position-relative"
            data-bs-ride="carousel"
          >
            <img
              src={hero1}
              className="heroimg vh-100 w-100 object-fit-cover"
              alt="WSTSC students in traditional dress seated with their families at a school gathering"
              fetchPriority="high"
            />

            <div className="carousel-indicators z-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  data-bs-target="#heroCarousel"
                  data-bs-slide-to={index}
                  className={index === 0 ? "active" : ""}
                  aria-current={index === 0 ? "true" : undefined}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>

            <div className="overlay position-absolute top-0 start-0 w-100 h-100"></div>

            <div className="carousel-inner container position-absolute top-50 start-50 translate-middle w-100 px-3 px-sm-5 z-2">
              <div className="text-center text-white mb-4 opacity-75">
                <h2 className="fs-6 fw-bold text-uppercase mb-1">
                  Tamil classes for children in Sydney
                </h2>
                <h1 className="fs-6 fw-normal mb-0">
                  Western Sydney Tamil Study Centre
                </h1>
              </div>
              {heroSlides.map((hero, index) => (
                <div
                  key={index}
                  className={`carousel-item text-center text-white ${
                    index === 0 ? "active" : ""
                  }`}
                >
                  <div className="fs-2 fw-bold mb-3 text-uppercase" lang="ta">
                    {hero.content}
                  </div>

                  <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => handleNavClick("#about")}
                      aria-label="Read more about us"
                      className="btn blue-bg fw-bold text-white rounded-5 px-4 py-2 fs-6 border-0"
                    >
                      READ MORE
                    </button>

                    <button
                      type="button"
                      onClick={() => handleNavClick("#contact")}
                      aria-label="Contact us"
                      className="btn red-bg fw-bold text-white rounded-5 px-4 py-2 fs-6 border-0 text-uppercase"
                    >
                      CONTACT US
                    </button>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="/enrolment/"
                      className="btn fw-bold text-white rounded-5 px-4 py-2 fs-6 border-0 text-uppercase"
                      style={{ backgroundColor: "#ff6d07" }}
                    >
                      Enrol Now
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="carousel-control-prev"
              type="button"
              data-bs-target="#heroCarousel"
              data-bs-slide="prev"
              aria-label="Previous slide"
            >
              <i className="bi bi-arrow-left-short fs-1 px-2 arrow-icon d-none d-sm-block text-white border cursor"></i>
            </button>
            <button
              className="carousel-control-next"
              type="button"
              data-bs-target="#heroCarousel"
              data-bs-slide="next"
              aria-label="Next slide"
            >
              <i className="bi bi-arrow-right-short fs-1 px-2 arrow-icon d-none d-sm-block text-white border cursor"></i>
            </button>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="about-section py-5" ref={aboutRef}>
          <div className="container mb-0 mb-md-5 py-5">
            <motion.div
              className="row align-items-center mb-5"
              variants={staggerContainer}
              initial="hidden"
              animate={aboutInView ? "visible" : "hidden"}
            >
              <motion.div
                className="col-lg-6 position-relative mb-5 mb-lg-0"
                variants={slideInLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <motion.div
                  className="main-img-container"
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <motion.img
                    src={mainImg}
                    alt={imageAlts[mainImg]}
                    className="img-fluid rounded-5 main-img"
                    initial={false}
                    animate={{ opacity: [0, 1], x: [-20, 0] }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </motion.div>

                <motion.img
                  src={smallImg}
                  alt={imageAlts[smallImg]}
                  className="img-fluid rounded-3 small-img position-absolute border border-white border-5 rounded-5"
                  initial={false}
                  animate={{ opacity: [0, 1], x: [20, 0] }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </motion.div>

              <motion.div
                className="col-lg-6 ps-auto ps-md-5"
                variants={slideInRight}
              >
                <motion.p
                  className="text-uppercase fw-bold red-text mb-2"
                  variants={fadeInUp}
                >
                  About<span className="blue-text position-relative"> Us</span>
                </motion.p>
                <motion.h2
                  className="display-5 fw-bold mb-3"
                  variants={fadeInUp}
                >
                  Western Sydney Tamil Study Centre (WSTSC)
                </motion.h2>
                <motion.p className="text-muted mb-3" variants={fadeInUp}>
                  Western Sydney Tamil Study Centre is a not-for-profit
                  community language school established to support Tamil
                  language education outside mainstream school hours. We work
                  closely with parents, volunteers, and community leaders to
                  deliver structured and age-appropriate Tamil programs. <br />{" "}
                  <br />
                  We provide a safe, inclusive, and engaging learning
                  environment where students develop strong Tamil language
                  skills, cultural identity, and confidence, while building
                  meaningful connections with the wider Tamil community in
                  Western Sydney.
                </motion.p>

                <motion.p
                  className="border-start border-4 border-danger ps-3 fst-italic text-muted small mb-4"
                  variants={fadeInUp}
                >
                  Our curriculum focuses on listening, speaking, reading, and
                  writing, alongside Tamil history, values, and cultural
                  traditions, supporting students at every stage of their
                  learning journey.
                </motion.p>

                <motion.div variants={fadeInUp}>
                  <CustomButton
                    text="Contact Us"
                    icon={<i className="bi bi-arrow-right "></i>}
                    onClick={() => handleNavClick("#contact")}
                  />
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Explore Section - links to the detailed parent pages */}
        <section className="py-5 border-top">
          <div className="container">
            <h2 className="fw-bold text-center mb-4">
              <span className="red-text">Plan your child's </span>
              <span className="blue-text">Tamil learning</span>
            </h2>
            <div className="row g-4">
              {exploreLinks.map((item) => (
                <div className="col-sm-6 col-lg-3" key={item.to}>
                  <Link
                    to={item.to}
                    className="d-block h-100 bg-white border rounded-4 border-bottom-left p-4 text-decoration-none text-dark explore-card"
                  >
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <i className={`bi ${item.icon} fs-2 blue-text flex-shrink-0`} aria-hidden="true"></i>
                      <h3 className="fs-5 fw-bold mb-0 red-text">{item.title}</h3>
                    </div>
                    <p className="text-muted mb-0 small">{item.text}</p>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Goals Section */}
        <section className="py-5" style={{ background: "#f9f9f9" }}>
          <div className="container">
            <motion.div
              className="row g-4 justify-content-center align-items-stretch"
              // variants={staggerContainer}
              // initial="hidden"
              // animate={teamInView ? "visible" : "hidden"}
            >
              {/* Vision Card */}
              <motion.div
                className="col-12 col-lg-4 d-flex"
                variants={slideInLeft}
              >
                <div className="vmv-card position-relative bg-white rounded-5 overflow-hidden w-100 mh-100">
                  <div
                    className="position-relative overflow-hidden"
                    style={{ height: "250px" }}
                  >
                    <img
                      src={school}
                      alt="WSTSC students performing on stage for families in a school hall"
                      className="w-100 h-00"
                      style={{
                        objectFit: "cover",
                      }}
                    />

                    <div className="red-curve-wrap">
                      <div className="red-curve"></div>
                    </div>
                    <div className="main-icon vision-main-icon red-soft z-3 rounded-circle d-flex align-items-center justify-content-center position-absolute">
                      <img
                        src={visionIcon}
                        alt="Vision icon"
                        style={{
                          width: "50px",
                          height: "50px",
                        }}
                      />
                    </div>
                  </div>

                  <div className="vision-content py-3 px-5 position-relative text-center">
                    <p
                      className="fw-bolder fs-6 mb-1 red-text"
                      style={{
                        letterSpacing: "0.5px",
                      }}
                    >
                      OUR
                    </p>
                    <h3
                      className="red-text fs-2 fw-bolder mb-2"
                      style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                      }}
                    >
                      VISION
                    </h3>
                    <div className="title-line red-bg"></div>

                    <p className="text-muted m-0 position-relative z-1 fs-5 lh-base">
                      To preserve and strengthen the Tamil language and culture
                      while empowering young people to succeed academically and
                      socially in multicultural Australia.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Mission + Values Cards */}
              {aboutData?.map((item, index) => {
                const isMission = item.title.toLowerCase().includes("mission");

                const colorText = isMission ? "blue-text" : "red-text";
                const colorBg = isMission ? "blue-bg" : "red-bg";
                const softBg = isMission ? "blue-soft" : "red-soft";

                const headerIconImage = isMission ? missionIcon : valuesIcon;
                const headerIconAlt = isMission
                  ? "Mission icon"
                  : "Values icon";

                return (
                  <motion.div
                    key={index}
                    className="col-12 col-lg-4 d-flex"
                    variants={isMission ? scaleIn : slideInRight}
                  >
                    <div
                      className="vmv-card vmv-card position-relative bg-white rounded-5 overflow-hidden w-100 mh-100"
                      style={{ padding: "42px 34px" }}
                    >
                      <div className="d-flex align-items-center gap-4 mb-4">
                        <div
                          className={`header-icon rounded-circle d-flex align-items-center justify-content-center ${softBg}`}
                          style={{
                            width: "92px",
                            height: "92px",
                          }}
                        >
                          <img
                            src={headerIconImage}
                            alt={headerIconAlt}
                            style={{
                              width: "60px",
                              height: "60px",
                            }}
                          />
                        </div>

                        <div>
                          <p
                            className={`fw-bolder fs-6 mb-1 ${colorText}`}
                            style={{
                              letterSpacing: "0.5px",
                            }}
                          >
                            OUR
                          </p>
                          <h3
                            className={`fs-2 fw-bolder mb-2 ${colorText}`}
                            style={{
                              fontFamily: 'Georgia, "Times New Roman", serif',
                            }}
                          >
                            {item.title.replace("Our ", "").toUpperCase()}
                          </h3>
                          <div className={`title-line m-0 ${colorBg}`}></div>
                        </div>
                      </div>

                      <ul className="vmv-list list-unstyled p-0 m-0">
                        {item.content.map((point, i) => {
                          const hasColon = point.includes(":");
                          const [label, ...rest] = point.split(":");

                          return (
                            <li
                              key={i}
                              className="d-flex align-items-start text-muted fs-6 py-3 px-0 gap-3 lh-base"
                            >
                              <span
                                className={`list-icon rounded-circle lh-1 flex-shrink-0 text-white d-inline-flex align-items-center justify-content-center ${colorBg}`}
                                style={{
                                  width: "38px",
                                  height: "38px",
                                  minWidth: "38px",
                                  minHeight: "38px",
                                }}
                              >
                                <i className="bi bi-vector-pen fs-6 lh-1"></i>
                              </span>

                              <span>
                                {hasColon ? (
                                  <>
                                    <strong className="text-dark">
                                      {label}:
                                    </strong>{" "}
                                    {rest.join(":").trim()}
                                  </>
                                ) : (
                                  point
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="py-5 tabs-bg">
          <TamilDecor />
          <div className="container pb-5">
            <div className="bg-white rounded-4 p-3">
              <div
                className="row g-0 align-items-stretch border rounded-4 overflow-hidden"
                style={{ minHeight: "405px" }}
              >
                {/* Left Tabs */}
                <div className="col-lg-4">
                  <div className="tabs-sidebar bg-white border-right h-100 d-flex flex-row flex-lg-column">
                    {tabs.map((tab) => {
                      const isActive = tab.id === activeId;

                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveId(tab.id)}
                          aria-label={tab.title}
                          title={tab.title}
                          className={`tab-menu-item position-relative d-flex align-items-center justify-content-center justify-content-lg-start gap-lg-4 text-start fw-bold fs-6 w-100 bg-white ${
                            isActive ? "active" : ""
                          }`}
                        >
                          <span className="tab-icon d-flex align-items-center justify-content-center">
                            {tab.icon}
                          </span>

                          {/* Hide label on mobile and tablet, show only desktop */}
                          <span className="lh-1 d-none d-lg-inline">
                            {tab.title}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Right Content */}
                <div className="col-lg-8">
                  <div
                    className="tabs-content-area position-relative overflow-auto custom-scroll h-100"
                    style={{ maxHeight: "440px" }}
                  >
                    {activeTab?.isProgramTab ? (
                      activeTab?.content
                    ) : (
                      <div className="position-relative z-2">
                        <div className="d-flex flex-column flex-md-row align-items-center gap-4 mb-4">
                          <div className="tab-content-icon d-flex align-items-center justify-content-center blue-text rounded-circle">
                            {activeTab?.mainIcon}
                          </div>

                          <div>
                            <h3
                              className="blue-text fw-bolder fs-2 mb-3 lh-1"
                              style={{
                                fontFamily: 'Georgia, "Times New Roman", serif',
                              }}
                            >
                              {activeTab?.title}
                            </h3>
                            <div
                              className="blue-bg rounded-pill"
                              style={{ width: "64px", height: "3px" }}
                            ></div>
                          </div>
                        </div>

                        <p
                          className="text-muted mb-3 lh-base"
                          style={{ maxWidth: "600px" }}
                        >
                          {activeTab?.description}
                        </p>

                        {activeTab?.focusTitle && (
                          <h5 className="fs-6 fw-bolder mb-2">
                            {activeTab.focusTitle}
                          </h5>
                        )}

                        <ul className="list-unstyled p-0 m-0">
                          {activeTab?.points?.map((point, index) => (
                            <li
                              key={index}
                              className="d-flex align-items-center text-muted fs-6 fw-medium gap-3 lh-base mb-2"
                            >
                              <span className="fs-6">
                                <i className="bi bi-check2-circle blue-text"></i>
                              </span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section
          id="team"
          className="position-relative bg-white py-5"
          ref={teamRef}
        >
          <div className="container position-relative py-5">
            <motion.div
              className="mb-4 text-center d-flex flex-column align-items-center justify-content-center"
              // variants={fadeInUp}
              // initial="hidden"
              // animate={teamInView ? "visible" : "hidden"}
            >
              <img src={leaf} alt="" style={{ height: "30px" }} />

              <h2
                className="h1 fw-bold lh-base m-0"
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}
              >
                <span className="red-text">Our </span>
                <span className="blue-text">Teachers</span>
              </h2>

              <div
                className="team-title-line red-bg rounded-pill mt-2"
                style={{ width: "70px", height: "4px" }}
              ></div>
            </motion.div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading teachers...</p>
              </div>
            )}

            {/* Team Cards */}
            {!loading && (
              <motion.div
                className="row g-4 align-items-stretch position-relative z-3"
                variants={staggerContainer}
                initial="hidden"
                animate={teamInView ? "visible" : "hidden"}
              >
                {/* Principal Card */}
                {principal && (
                  <motion.div
                    className="col-12 col-lg-4 mt-5 mt-md-0"
                    variants={slideInLeft}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="principal-card mt-5 mt-md-0 position-relative border border-4 border-white red-soft rounded-4 shadow-sm text-center d-flex flex-column align-items-center justify-content-center">
                      <div className="d-flex justify-content-center p-4">
                        <img
                          src={principal.photo_url || defaultImg}
                          alt={principal.full_name}
                          className="w-100 h-100 rounded-circle object-fit-cover border border-white border-4"
                          style={{ width: "300px", height: "300px" }}
                        />
                      </div>

                      <div className="d-flex flex-column p-4 align-items-center justify-content-center">
                        <h4
                          className="red-text fw-bold"
                          style={{
                            fontFamily: 'Georgia, "Times New Roman", serif',
                          }}
                        >
                          {principal.full_name}
                        </h4>

                        <h5 className="mb-4 text-muted fw-semibold">
                          Principal{" "}
                        </h5>
                        <div
                          className="rounded-pill red-bg"
                          style={{ height: "3px", width: "58px" }}
                        ></div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Teachers Slider */}
                {teachers.length > 0 && (
                  <motion.div
                    className={principal ? "col-12 col-lg-8" : "col-12"}
                    variants={slideInRight}
                  >
                    <Swiper
                      modules={[Navigation, Autoplay]}
                      spaceBetween={28}
                      slidesPerView={1}
                      loop={teachers.length > 3}
                      autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }}
                      navigation={{
                        nextEl: ".swiper-button-next-custom",
                        prevEl: ".swiper-button-prev-custom",
                      }}
                      breakpoints={{
                        576: {
                          slidesPerView: 1,
                          spaceBetween: 22,
                        },
                        768: {
                          slidesPerView: 2,
                          spaceBetween: 24,
                        },
                        992: {
                          slidesPerView: 3,
                          spaceBetween: 28,
                        },
                      }}
                      className="py-2"
                    >
                      {teachers.map((teacher, index) => {
                        const isGreenCard = index % 2 === 0;

                        return (
                          <SwiperSlide key={teacher.tid}>
                            <motion.div
                              className={`position-relative border border-4 border-white p-3 h-100 rounded-4 shadow-sm text-center overflow-hidden ${
                                isGreenCard ? "green-soft" : "red-soft"
                              }`}
                              variants={scaleIn}
                              // whileHover={{ y: -6 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div
                                className="d-flex justify-content-center align-items-center position-relative rounded-circle border border-white border-4 mx-auto"
                                style={{ width: "164px", height: "164px" }}
                              >
                                <img
                                  src={teacher.photo_url || defaultImg}
                                  alt={teacher.full_name}
                                  className="w-100 h-100 object-fit-cover rounded-circle"
                                />

                                <span className="teacher-leaf-img position-absolute">
                                  <img
                                    src={leaf}
                                    alt=""
                                    style={{ height: "30px" }}
                                  />
                                </span>
                              </div>

                              <div className="d-flex flex-column p-4 align-items-center justify-content-center">
                                <h5
                                  className={
                                    isGreenCard
                                      ? "blue-text fw-bold"
                                      : "red-text fw-bold"
                                  }
                                  style={{
                                    fontFamily:
                                      'Georgia, "Times New Roman", serif',
                                  }}
                                >
                                  {teacher.full_name}
                                </h5>

                                <p className="text-muted fw-semibold text-capitalize">
                                  {teacher.position_display_name || "Teacher"}
                                </p>

                                <div
                                  className={`rounded-pill ${
                                    isGreenCard ? "blue-bg" : "red-bg"
                                  }`}
                                  style={{ height: "3px", width: "58px" }}
                                ></div>
                              </div>
                            </motion.div>
                          </SwiperSlide>
                        );
                      })}
                    </Swiper>

                    {/* Custom Navigation Buttons - unchanged */}
                    <div className="d-flex justify-content-center justify-content-md-end mt-4 gap-2">
                      <button
                        className="swiper-button-prev-custom custom-arrow-indicator d-flex align-items-center justify-content-center bg-transparent"
                        aria-label="Previous teacher"
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>

                      <button
                        className="swiper-button-next-custom custom-arrow-indicator d-flex align-items-center justify-content-center bg-transparent"
                        aria-label="Next teacher"
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                  </motion.div>
                )}

                {!principal && teachers.length === 0 && (
                  <div className="text-center py-5 w-100">
                    <p className="text-muted">No teachers found.</p>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Bottom Wave */}
          <div className="team-bottom-wave">
            <div className="team-blue-wave"></div>
            <div className="team-red-wave"></div>
          </div>
        </section>

        {/* Events Section */}
        <section
          id="events"
          className="position-relative tabs-bg py-5"
          ref={eventsRef}
        >
          <TamilDecor />
          <div className="container position-relative py-5">
            <motion.div className="mb-4 text-center d-flex flex-column align-items-center justify-content-center">
              <img src={leaf} alt="" style={{ height: "30px" }} />

              <h2
                className="h1 fw-bold lh-base m-0"
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}
              >
                <span className="red-text">Upcoming </span>
                <span className="blue-text">Events</span>
              </h2>

              <div
                className="team-title-line red-bg rounded-pill mt-2"
                style={{ width: "70px", height: "4px" }}
              ></div>
            </motion.div>

            {/* Loading State */}
            {eventsLoading && (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading events...</p>
              </div>
            )}

            {/* Events Cards */}
            {!eventsLoading && events.length > 0 && (
              <motion.div
                className="row g-4 align-items-stretch position-relative z-3"
                variants={staggerContainer}
                initial="hidden"
                animate={eventsInView ? "visible" : "hidden"}
              >
                <div className="col-12">
                  <Swiper
                    modules={[Navigation, Autoplay]}
                    spaceBetween={28}
                    slidesPerView={1}
                    loop={events.length > 3}
                    autoplay={{
                      delay: 5000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    navigation={{
                      nextEl: ".event-swiper-button-next-custom",
                      prevEl: ".event-swiper-button-prev-custom",
                    }}
                    breakpoints={{
                      576: {
                        slidesPerView: 1,
                        spaceBetween: 22,
                      },
                      768: {
                        slidesPerView: 2,
                        spaceBetween: 24,
                      },
                      992: {
                        slidesPerView: 3,
                        spaceBetween: 28,
                      },
                    }}
                    // Centre the cards only when they all fit on screen (e.g. 2
                    // events on desktop). Forcing justify-content: center when
                    // they don't fit (phones) broke the slide positions.
                    centerInsufficientSlides
                    watchOverflow
                    className="py-2"
                  >
                    {events.map((event, index) => {
                      const isGreenCard = index % 2 === 0;

                      return (
                        <SwiperSlide key={event.aid}>
                          <motion.div
                            className={`event-card position-relative border border-4 border-white p-3 h-100 rounded-4 shadow-sm text-center overflow-hidden ${
                              isGreenCard ? "green-soft" : "red-soft"
                            }`}
                            variants={scaleIn}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <div className="event-card-img-wrap position-relative rounded-4 overflow-hidden mx-auto">
                              <img
                                src={getEventImage(event)}
                                alt={event.article_title}
                                className="event-card-img w-100 h-100 object-fit-cover"
                              />

                              {event.event_venue?.start_date && (
                                <div className="event-date-badge position-absolute rounded-3 text-center">
                                  <span
                                    className={`event-date-day d-block fw-bold lh-1 ${
                                      isGreenCard ? "blue-text" : "red-text"
                                    }`}
                                  >
                                    {getEventDateParts(
                                      event.event_venue.start_date,
                                    ).day}
                                  </span>
                                  <span className="event-date-month d-block text-uppercase text-muted">
                                    {getEventDateParts(
                                      event.event_venue.start_date,
                                    ).month}
                                  </span>
                                </div>
                              )}

                              <span className="teacher-leaf-img position-absolute">
                                <img
                                  src={leaf}
                                  alt=""
                                  style={{ height: "30px" }}
                                />
                              </span>
                            </div>

                            <div className="d-flex flex-column p-4 align-items-center justify-content-center">
                              <h5
                                className={
                                  isGreenCard
                                    ? "blue-text fw-bold"
                                    : "red-text fw-bold"
                                }
                                style={{
                                  fontFamily:
                                    'Georgia, "Times New Roman", serif',
                                }}
                              >
                                {event.article_title}
                              </h5>

                              {event.event_venue?.venue && (
                                <p className="text-muted small mb-2 event-card-meta">
                                  <i className="bi bi-geo-alt me-1"></i>
                                  {event.event_venue.venue}
                                </p>
                              )}

                              {event.article_shortdesc && (
                                <p className="text-muted small mb-3">
                                  {truncateText(event.article_shortdesc, 90)}
                                </p>
                              )}

                              <div
                                className={`rounded-pill mb-3 ${
                                  isGreenCard ? "blue-bg" : "red-bg"
                                }`}
                                style={{ height: "3px", width: "58px" }}
                              ></div>

                              <Link
                                to={buildEventPath(event)}
                                className={`event-card-btn btn btn-sm rounded-pill text-white fw-semibold px-4 border-0 ${
                                  isGreenCard ? "blue-bg" : "red-bg"
                                }`}
                              >
                                View Details
                                <span className="visually-hidden">
                                  : {event.article_title}
                                </span>
                              </Link>
                            </div>
                          </motion.div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>

                  {/* Custom Navigation Buttons */}
                  <div className="d-flex justify-content-center justify-content-md-end mt-4 gap-2">
                    <button
                      className="event-swiper-button-prev-custom custom-arrow-indicator d-flex align-items-center justify-content-center bg-transparent"
                      aria-label="Previous event"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>

                    <button
                      className="event-swiper-button-next-custom custom-arrow-indicator d-flex align-items-center justify-content-center bg-transparent"
                      aria-label="Next event"
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {!eventsLoading && events.length === 0 && (
              <div className="text-center py-5 w-100">
                <p className="text-muted">No upcoming events found.</p>
              </div>
            )}
          </div>
        </section>

        {/* Social Section - curated YouTube videos and official social links */}
        <SocialSection />

        {/* Google reviews - live from Google via public/google-reviews.php */}
        <GoogleReviews />

        {/* Google Map */}
        <motion.div
          className="p-0 m-0"
          initial={{ opacity: 0 }}
          animate={contactInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <iframe
            title="Map of Western Sydney Tamil Study Centre at Walters Road Public School, Blacktown"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3316.07225653402!2d150.8920656!3d-33.7846322!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b1299c4294db183%3A0x850500e2466c825!2sWestern%20Sydney%20Tamil%20Study%20Centre!5e0!3m2!1sen!2slk!4v1777091431059!5m2!1sen!2slk"
            width="100%"
            height="350"
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.div>

        {/* Contact Section */}
        <div ref={contactRef}>
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default Home;
